import type { GroupRecord, StreamRecord } from '@vlabs/api/artemy/model';
import { get, has, isEqual } from 'lodash';

// Пути sync-иконок полей настроек источника — единый контракт для
// getStreamSettingsSyncStatus и полевых компонентов формы.
export const STREAM_SETTINGS_SYNC_FIELDS = {
  timestampSource: 'data.timestamp_source',
  ptsStartTime: 'data.pts.start_time',
  rotation: 'data.rotation',
  restart: 'autorestart.restart',
  attemptCount: 'autorestart.attempt_count',
  delay: 'autorestart.delay',
  location: 'location',
} as const;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return (typeof value === 'object' && value !== null && !Array.isArray(value))
    ? value as Record<string, unknown>
    : undefined;
}

// Пофилдовые статусы синхронизации настроек источника с группой:
// путь → true — личное значение совпадает с групповым (или поля нет в личных
// данных — наследуется), false — личное значение отличается.
// Отсутствие пути в результате — иконка не показывается
// (параметр не наследуется от группы).
export function getStreamSettingsSyncStatus(
  group: GroupRecord | undefined,
  stream: StreamRecord | undefined,
): Record<string, boolean> {
  const status: Record<string, boolean> = {};
  if (!group || !stream) return status;

  const groupSettings = group.stream_settings ?? {};
  const data = asRecord(groupSettings.data);
  const autorestart = asRecord(groupSettings.autorestart);
  const hasLocation = !!groupSettings.location;

  const groupPts = asRecord(data?.pts);
  const groupDefined: Record<string, boolean> = {
    [STREAM_SETTINGS_SYNC_FIELDS.timestampSource]: 'timestamp_source' in (data ?? {}),
    [STREAM_SETTINGS_SYNC_FIELDS.ptsStartTime]: !!groupPts && 'start_time' in groupPts,
    [STREAM_SETTINGS_SYNC_FIELDS.rotation]: 'rotation' in (data ?? {}),
    [STREAM_SETTINGS_SYNC_FIELDS.restart]: 'restart' in (autorestart ?? {}),
    [STREAM_SETTINGS_SYNC_FIELDS.attemptCount]: 'attempt_count' in (autorestart ?? {}),
    [STREAM_SETTINGS_SYNC_FIELDS.delay]: 'delay' in (autorestart ?? {}),
  };

  Object.entries(groupDefined).forEach(([path, defined]) => {
    if (!defined) return;

    status[path] = !has(stream, path)
      || isEqual(get(stream, path), get(groupSettings, path));
  });

  if (hasLocation) {
    // Геопозиция — один параметр (адрес и координаты — разные ключи одного блока):
    // статус по равенству собственного location групповому; пустой собственный
    // location приравнивается к отсутствию (наследует).
    const streamLocation = asRecord(stream.location);
    const hasOwnLocation = !!streamLocation && Object.keys(streamLocation).length > 0;

    status[STREAM_SETTINGS_SYNC_FIELDS.location] = !hasOwnLocation
      || isEqual(stream.location, groupSettings.location);
  }

  return status;
}
